import { VFC, useState, Fragment, useRef, useEffect } from "react";
import { Box, Button, TableContainer, Table, TableRow, TableHead, TableSortLabel, TableCell, TableBody, TablePagination, Tooltip } from "@mui/material";
import { PersonOff, Done, AccountTree, Lock, PhonelinkLock } from "@mui/icons-material";
import { Tag } from "../../../entity/Tag";
import { Utility } from "../../../utils/Utility";
import { ACCESS_AUTH, LOCK_STATUS, TENANT_LOCK, IS_MFAAUTH } from "../../../entity/MdmUser";
import { MdmUserSearchResponse } from "../../mdmuser/api/MdmUserForm";

/***************************************************************
 * ヘッダー
 **************************************************************/
interface HeadCell {
    key: keyof MdmUserSearchResponse;
    value: string;
    icon?: React.ReactElement;
    isSort?: Boolean;
}

const headCells: readonly HeadCell[] = [
    {
        key: "tenantName",
        value: "テナント名",
        isSort: true,
    },
    {
        key: "fullName",
        value: "氏名",
        isSort: true,
    },
    {
        key: "email",
        value: "メールアドレス",
        isSort: true,
    },
    {
        key: "accessAuth",
        value: "アクセス権限",
        isSort: true,
    },
    {
        key: "roleName",
        value: "権限",
        isSort: true,
    },
    {
        key: "tenantLock",
        value: "テナントロック",
        icon: <AccountTree fontSize="small" />,
        isSort: true,
    },
    {
        key: "lockStatus",
        value: "ロックステータス",
        icon: <Lock fontSize="small" />,
        isSort: true,
    },
    {
        key: "isMfaauth",
        value: "MFA認証",
        icon: <PhonelinkLock fontSize="small" />,
        isSort: true,
    },
    {
        key: "expireDate",
        value: "有効期限",
        isSort: true,
    },
    {
        key: "lastloginDate",
        value: "最終ログイン日時",
        isSort: true,
    },
    {
        key: "tag",
        value: "タグ",
        isSort: true,
    },
];
// テーブルの総列数（データ列 + アクション列）
const dataCellColumns = headCells.length + 1;

interface HeadTableProps {
    onRequestSort: (
        event: React.MouseEvent<unknown>,
        property: keyof MdmUserSearchResponse
    ) => void;
    order: Order;
    orderBy: string;
}

/***************************************************************
 * ソート
 **************************************************************/
type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    // null/undefined の処理
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // 日付フィールドは文字列比較（ISO形式なので辞書順 = 時系列順）
    const isDateField = orderBy === "expireDate" || orderBy === "lastloginDate";
    if (isDateField) {
        const aStr = String(aValue);
        const bStr = String(bValue);
        if (bStr < aStr) return -1;
        if (bStr > aStr) return 1;
        return 0;
    }

    // タグフィールドはすべてのタグ名を連結してソート
    const isTagField = orderBy === "tag";
    if (isTagField) {
        const aTagStr = Array.isArray(aValue) 
            ? aValue.map((t: any) => t.tagName).join(",")
            : "";
        const bTagStr = Array.isArray(bValue)
            ? bValue.map((t: any) => t.tagName).join(",")
            : "";
        if (bTagStr < aTagStr) return -1;
        if (bTagStr > aTagStr) return 1;
        return 0;
    }

    // その他のフィールドは元の型のまま比較
    if (bValue < aValue) {
        return -1;
    }
    if (bValue > aValue) {
        return 1;
    }
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
): (
    a: { [key in Key]: string | number | boolean | Tag[] },
    b: { [key in Key]: string | number | boolean | Tag[] }
) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

/***************************************************************
 * アイコン表示
 **************************************************************/
// テナントロックアイコン取得
const getTenantLockIcon = (tenantLock: boolean) => {
    return tenantLock ? (
        <Tooltip title={TENANT_LOCK[String(tenantLock)]}>
            <PersonOff fontSize="small" color="error" />
        </Tooltip>
    ) : "-";
};

// ロックステータスアイコン取得
const getLockStatusIcon = (lockStatus: string) => {
    switch (lockStatus) {
        case "1":
            return (
                <Tooltip title={LOCK_STATUS[lockStatus]}>
                    <PersonOff fontSize="small" color="error" />
                </Tooltip>
            );
        case "2":
            return (
                <Tooltip title={LOCK_STATUS[lockStatus]}>
                    <PersonOff fontSize="small" color="warning" />
                </Tooltip>
            );
        case "0":
            return "-";
        default:
            return "-";
    }
};

// MFA認証アイコン取得
const getMfaauthIcon = (mfaAuth: boolean) => {
    return mfaAuth ? (
        <Tooltip title={IS_MFAAUTH[String(mfaAuth)]}>
            <Done fontSize="small" color="success" />
        </Tooltip>
    ) : "-";
};

// ツールチップ付きで表示するセル
// 省略されている場合のみツールチップを表示する
const CellWithTooltip = ({ children }: { children: React.ReactNode }) => {
    const [isOverflow, setIsOverflow] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const checkOverflow = () => {
            if (ref.current) {
                setIsOverflow(ref.current.scrollWidth > ref.current.clientWidth);
            }
        };

        // 初回チェック
        checkOverflow();

        // ResizeObserver で要素のサイズ変更を監視
        const observer = new ResizeObserver(checkOverflow);
        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [children]);

    return (
        <Tooltip title={isOverflow ? String(children) : ""}>
            <span 
                ref={ref}
                style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
                {children}
            </span>
        </Tooltip>
    );
};

/***************************************************************
 * ヘッダー - レイアウト
 **************************************************************/
const Head = (props: HeadTableProps) => {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler =
        (property: keyof MdmUserSearchResponse) =>
        (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
        <TableRow>
            {headCells.map((header, index: number) => (
                <TableCell key={index}>
                    {header.isSort ? (
                        <TableSortLabel
                            active={orderBy === header.key}
                            direction={orderBy === header.key ? order : "asc"}
                            onClick={createSortHandler(header.key)}
                        >
                            {header.icon ? (
                                <Tooltip title={header.value}>
                                    <span>{header.icon}</span>
                                </Tooltip>
                            ) : (
                                header.value
                            )}
                        </TableSortLabel>
                    ) : (
                        header.value
                    )}
                </TableCell>
            ))}
            <TableCell key={headCells.length}></TableCell>
        </TableRow>
        </TableHead>
    );
};

/***************************************************************
 * 一覧 - レイアウト
 **************************************************************/
type Props = {
    mdmUserList: MdmUserSearchResponse[];
    onEditButtonClick: (mdmUserId: string) => void;
    resetPage?: number;
};
export const MdmUserTable: VFC<Props> = ({ mdmUserList: mdmUserList, onEditButtonClick, resetPage = 0 }) => {
    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof MdmUserSearchResponse>("tenantName");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // resetPageが変更されたときにページをリセット
    useEffect(() => {
        setPage(0);
    }, [resetPage]);

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof MdmUserSearchResponse
    ) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    /***************************************************************
     * ページング処理
     **************************************************************/
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - mdmUserList.length) : 0;
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{ p: 2, mt: -3 }}>
            <TableContainer>
                <Table size="small">
                    <Head
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                        {stableSort(mdmUserList, getComparator(order, orderBy))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((mdmUser, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableCell sx={{ minWidth: 120, maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            <CellWithTooltip>{mdmUser.tenantName}</CellWithTooltip>
                                        </TableCell>
                                        <TableCell sx={{ minWidth:  80, maxWidth: 100, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            <CellWithTooltip>{mdmUser.fullName}</CellWithTooltip>
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 140, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            <CellWithTooltip>{mdmUser.email}</CellWithTooltip>
                                        </TableCell>
                                        <TableCell sx={{ minWidth:  80, maxWidth: 100, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ACCESS_AUTH[mdmUser.accessAuth as number] || mdmUser.accessAuth}</TableCell>
                                        <TableCell sx={{ minWidth: 120, maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mdmUser.roleName}</TableCell>
                                        <TableCell>{getTenantLockIcon(mdmUser.tenantLock as boolean)}</TableCell>
                                        <TableCell>{getLockStatusIcon(mdmUser.lockStatus as string)}</TableCell>
                                        <TableCell>{getMfaauthIcon(mdmUser.isMfaauth as boolean)}</TableCell>
                                        <TableCell>{mdmUser.expireDate ? Utility.dateFormat(mdmUser.expireDate as string) : '-'}</TableCell>
                                        <TableCell>{mdmUser.lastloginDate ? Utility.dateFormatYmdHis(mdmUser.lastloginDate as string) : '-'}</TableCell>
                                        <TableCell sx={{ minWidth:  80, maxWidth: 100, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {Array.isArray(mdmUser.tag) && mdmUser.tag.length > 0
                                                ? mdmUser.tag.map((tag: any, tagIndex: number) => (
                                                    <CellWithTooltip key={tagIndex}>
                                                        {0 === tagIndex ? tag.tagName : ", " + tag.tagName}
                                                    </CellWithTooltip>
                                                ))
                                                : ""
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                sx={{ fontSize: "0.75rem", minWidth: "50px", padding: "4px 8px" }}
                                                onClick={() => onEditButtonClick(mdmUser.id as string)}
                                            >
                                                編集
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        {emptyRows > 0 && (
                            <TableRow>
                                <TableCell colSpan={dataCellColumns} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={mdmUserList.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};
